// Finance build — the loan Financials tab's Income & Outflow panel. A read-only
// roll-up of the loan's recognized income events: Phoenix earnings (PhoenixFee +
// Baseline), seller/broker/servicer outflows (PaidOut), and pass-through cost
// (Reimbursement + Processor), each with expected-vs-received totals and a
// variance. Reversed events are excluded server-side. Mirrors the visual
// approach of PaymentTrackingTab (summary strip + grouped Table).

import { Card, EmptyState, Skeleton, Table, THead, TBody, TR, TH, TD } from "@/design-system/components/ui";
import { formatCurrency } from "@/design-system/lib/format";
import { useLoanIncomeLedger } from "@/hooks/portfolioAdmin/useIncomeLedger";
import type {
  IncomeLedgerGroup,
  IncomeLedgerRowDto,
} from "@/api/portfolioAdmin/incomeLedgerApi";

interface IncomeOutflowPanelProps {
  loanId: string;
}

const GROUP_LABEL: Record<IncomeLedgerGroup, string> = {
  earnings: "Phoenix earnings",
  outflow: "Outflows",
  passthrough: "Pass-through cost",
};

const GROUP_ORDER: IncomeLedgerGroup[] = ["earnings", "outflow", "passthrough"];

// Classification codes → human labels. Falls back to the raw code if a new
// classification slips in (display-only; never reclassifies).
const CLASSIFICATION_LABEL: Record<string, string> = {
  PhoenixFee: "Phoenix fee",
  Baseline: "Baseline yield",
  PaidOut: "Paid out",
  Reimbursement: "Reimbursement",
  Processor: "Processor / servicer",
};

function classificationLabel(code: string): string {
  return CLASSIFICATION_LABEL[code] ?? code;
}

function SummaryTile({
  label,
  expected,
  received,
}: {
  label: string;
  expected: number;
  received: number;
}) {
  return (
    <div className="flex flex-col gap-space-1">
      <span className="font-display text-[10px] font-semibold uppercase tracking-label text-gold">
        {label}
      </span>
      <span className="font-display font-bold text-h3 text-onyx m-0">
        {formatCurrency(received)}
      </span>
      <span className="text-small text-gray-mid">
        of {formatCurrency(expected)} expected
      </span>
    </div>
  );
}

export function IncomeOutflowPanel({ loanId }: IncomeOutflowPanelProps) {
  const query = useLoanIncomeLedger(loanId);

  if (query.isLoading) {
    return (
      <Card data-testid="income-outflow-loading">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-5 w-full mt-space-3" />
        <Skeleton className="h-5 w-full mt-space-2" />
        <Skeleton className="h-5 w-2/3 mt-space-2" />
      </Card>
    );
  }

  if (query.isError || !query.data) {
    return (
      <Card data-testid="income-outflow-error">
        <p className="text-small text-status-error m-0">
          Income &amp; outflow could not be loaded.
        </p>
      </Card>
    );
  }

  const ledger = query.data;

  if (ledger.rows.length === 0) {
    return (
      <EmptyState
        title="No recognized income yet"
        description="Income and outflow events are recognized as the loan's payments are processed. Nothing has posted to this loan's ledger so far."
      />
    );
  }

  const { summary } = ledger;

  return (
    <Card data-testid="income-outflow-panel">
      <div className="flex items-start justify-between gap-space-3 mb-space-4">
        <h3 className="font-display font-bold text-h3 m-0">Income &amp; outflow</h3>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-space-4 mb-space-5">
        <SummaryTile
          label="Phoenix earnings"
          expected={summary.earningsExpected}
          received={summary.earningsReceived}
        />
        <SummaryTile
          label="Outflows"
          expected={summary.outflowsExpected}
          received={summary.outflowsReceived}
        />
        <SummaryTile
          label="Pass-through cost"
          expected={summary.passThroughExpected}
          received={summary.passThroughReceived}
        />
      </div>

      <Table>
        <THead>
          <TR>
            <TH>Classification</TH>
            <TH>Revenue pool</TH>
            <TH className="text-right">Expected</TH>
            <TH className="text-right">Received</TH>
            <TH className="text-right">Variance</TH>
          </TR>
        </THead>
        <TBody>
          {GROUP_ORDER.flatMap((group) => {
            const rows = ledger.rows.filter((r) => r.group === group);
            if (rows.length === 0) return [];
            return [
              <TR key={`group-${group}`} data-testid={`income-group-${group}`}>
                <TD
                  colSpan={5}
                  className="font-display text-[10px] font-semibold uppercase tracking-label text-gray-mid bg-white-warm"
                >
                  {GROUP_LABEL[group]}
                </TD>
              </TR>,
              ...rows.map((row: IncomeLedgerRowDto, i) => {
                const variance = row.expectedAmount - row.receivedAmount;
                return (
                  <TR key={`${group}-${row.classification}-${row.revenuePoolCode ?? ""}-${i}`}>
                    <TD>{classificationLabel(row.classification)}</TD>
                    <TD className="text-gray-mid">
                      {row.revenuePoolName
                        ? `${row.revenuePoolCode} · ${row.revenuePoolName}`
                        : "—"}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {formatCurrency(row.expectedAmount)}
                    </TD>
                    <TD className="text-right tabular-nums">
                      {formatCurrency(row.receivedAmount)}
                    </TD>
                    <TD
                      className={
                        Math.abs(variance) >= 0.005
                          ? "text-right tabular-nums text-status-error"
                          : "text-right tabular-nums text-gray-mid"
                      }
                    >
                      {formatCurrency(variance)}
                    </TD>
                  </TR>
                );
              }),
            ];
          })}
        </TBody>
      </Table>
    </Card>
  );
}
